import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Megaphone, Heart, ChevronRight, LogOut, ArrowLeftRight } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { toast } from 'sonner';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isAuthed, isOrganizer, logout, setOrgMode } = useAuth();

  const handleLogout = async () => {
    if (window.confirm('Log out? You will need to sign in again to enquire.')) {
      await logout();
      toast.info('Logged out');
      navigate('/welcome');
    }
  };

  const switchToOrganizer = async () => {
    await setOrgMode(true);
    navigate('/organizer/dashboard');
  };

  return (
    <div className="max-w-2xl mx-auto px-6" data-testid="profile-page">
      <h1 className="text-2xl font-black text-gray-900 tracking-tight py-4">Profile</h1>

      {/* User Card */}
      {isAuthed ? (
        <div className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm" data-testid="profile-card">
          <div className="w-14 h-14 rounded-full bg-brand-magenta flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xl font-black">{user?.full_name?.[0]?.toUpperCase() || 'U'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-extrabold text-gray-900 truncate">{user?.full_name}</h2>
            <p className="text-sm text-gray-500 truncate">{user?.email}</p>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex-1">
            <h2 className="text-lg font-extrabold text-gray-900">Guest</h2>
            <p className="text-sm text-gray-500">Sign in to save events & enquire</p>
          </div>
          <button
            data-testid="profile-signin-btn"
            onClick={() => navigate('/login')}
            className="bg-brand-magenta text-white px-4 py-2.5 rounded-xl text-sm font-extrabold hover:bg-pink-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      )}

      {/* Switch to Organizer */}
      {isOrganizer && (
        <button
          data-testid="profile-switch-org-btn"
          onClick={switchToOrganizer}
          className="w-full mt-4 flex items-center justify-center gap-2 bg-pink-50 border border-pink-200 text-brand-magenta p-3 rounded-2xl font-extrabold text-sm hover:bg-pink-100 transition-colors"
        >
          <ArrowLeftRight className="w-4 h-4" />
          Switch to Organizer View
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Menu List */}
      <div className="mt-4 bg-white rounded-2xl border border-gray-100 overflow-hidden divide-y divide-gray-100">
        <button
          data-testid="profile-enquiries-btn"
          onClick={() => { if (!isAuthed) navigate('/login'); else navigate('/enquiries'); }}
          className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-pink-50 flex items-center justify-center">
              <MessageSquare className="w-4 h-4 text-brand-magenta" />
            </div>
            <span className="text-[15px] font-bold text-gray-900">My Enquiries</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>

        {!isOrganizer && (
          <button
            data-testid="profile-organizer-btn"
            onClick={() => { if (!isAuthed) navigate('/login'); else navigate('/organizer/apply'); }}
            className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                <Megaphone className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-[15px] font-bold text-gray-900">Apply as Organizer</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </button>
        )}

        <button
          onClick={() => navigate('/wishlist')}
          className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center">
              <Heart className="w-4 h-4 text-red-500" />
            </div>
            <span className="text-[15px] font-bold text-gray-900">Wishlist</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>

      </div>

      {isAuthed && (
        <button
          data-testid="profile-logout-btn"
          onClick={handleLogout}
          className="w-full mt-4 flex items-center justify-center gap-2 bg-gray-100 hover:bg-red-50 text-red-500 p-3.5 rounded-2xl font-extrabold text-[15px] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Log Out
        </button>
      )}

      <p className="text-center text-[11px] text-gray-400 mt-6 mb-8">RADIUS · v1.0.0</p>
    </div>
  );
}
